import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useMemo, useRef, useState } from 'react';
import { isFirefox } from 'react-device-detect';
import styled from 'styled-components';
import { useScheduledBroadcast } from '../../state/scheduleState';
import { useHasSupportedVideoCodec } from '../../utils';
import { useBroadcastUpdater, useBroadcasterMute, useIsUsingFrontCamera, useMediaStream, useMediaStreamContent, useMediaStreamMuteEffect, useRequestMediaPermissionsCallback, useVolumeController, } from '../../utils/media';
import { getStreamUrl } from '../../utils/url';
import { useStreamrTvBroadcaster } from '../../utils/useStreamrTvBroadcaster';
import { Anchor } from '../Anchor';
import { Button } from '../Button';
import { AudioDeviceButton, VideoDeviceButton } from '../DeviceButton';
import { FullscreenButton } from '../FullscreenButton';
import { Hud } from '../Hud';
import { LiveIndicator } from '../LiveIndicator';
import { LoadingIndicator } from '../LoadingIndicator';
import { CodecNotSupported, FirefoxNotSupported } from '../NotSupportedMessage';
import { Stage } from '../Stage';
import SvgIcon from '../SvgIcon';
import { Video } from '../Video';
import { showBroadcastPopup } from '../popups/BroadcastPopup';
import { BlockedPermissions, MobileView, ViewToolbarShareButtons, ViewToolbarShareUrlButton, } from './View';
export function BroadcasterViewMobile({ streamId, }) {
    const broadcaster = useStreamrTvBroadcaster(streamId);
    const { isAudioOn, isVideoOn, toggleAudio, toggleVideo } = useBroadcasterMute();
    const mediaStream = useMediaStream();
    useMediaStreamMuteEffect(mediaStream, {
        isAudioOn,
        isVideoOn,
    });
    const [broadcastStartedAt, setBroadcastStartedAt] = useState(undefined);
    const isBroadcasting = broadcastStartedAt !== undefined;
    useBroadcastUpdater({
        broadcaster,
        mediaStream,
        enabled: isBroadcasting,
    });
    const browserVideoCodecSupported = useHasSupportedVideoCodec();
    const { audio: hasAudio, video: hasVideo } = useMediaStreamContent(mediaStream);
    const requestMediaPermission = useRequestMediaPermissionsCallback();
    const hasTracks = useMemo(() => mediaStream.getTracks().length > 0, [mediaStream]);
    const settingsButtonAnchorRef = useRef(null);
    const canGoLive = hasTracks && !!broadcaster;
    const isUsingFrontCamera = useIsUsingFrontCamera(mediaStream);
    const [viewElement, setViewElement] = useState(null);
    const volumeControllerRef = useVolumeController(broadcaster);
    const broadcast = useScheduledBroadcast(streamId);
    if (isFirefox) {
        return _jsx(FirefoxNotSupported, {});
    }
    if (browserVideoCodecSupported === false) {
        return _jsx(CodecNotSupported, {});
    }
    return (_jsxs(_Fragment, { children: [_jsx(LoadingIndicator, { loading: !broadcaster }), _jsxs(MobileView, { innerRef: setViewElement, children: [_jsx(Stage, { children: (hasVideo || hasAudio) && (_jsx(Video, { fit: "cover", srcObject: mediaStream, autoPlay: true, playsInline: true, muted: true, mirrored: isUsingFrontCamera })) }), _jsxs(BroadcasterHud, { visible: true, children: [_jsxs(TopButtons, { children: [_jsxs("div", { children: [_jsx(ViewToolbarShareButtons, { children: _jsx(ViewToolbarShareUrlButton, { url: getStreamUrl(streamId, {
                                                        broadcastTitle: broadcast?.name,
                                                        broadcastStartsAt: broadcast?.startsAt,
                                                    }) }) }), _jsx(LiveIndicator, { startedAt: broadcastStartedAt, streamId: streamId })] }), _jsx("div", { children: _jsx(FullscreenButton, { element: viewElement }) })] }), _jsx("div", { children: !hasVideo && !hasAudio && (_jsx(BlockedPermissions, { children: _jsx(Button, { type: "button", kind: "secondary", size: "big", padding: "normal", onClick: () => {
                                            requestMediaPermission({
                                                audio: true,
                                                video: true,
                                            });
                                        }, children: "Allow microphone and\u00A0camera" }) })) }), _jsxs("div", { children: [_jsxs(BottomButtonsUpper, { children: [_jsx("div", {}), _jsx(GoLiveButton, { type: "button", kind: isBroadcasting ? 'destructive' : 'primary', shape: "pill", onClick: () => {
                                                    setBroadcastStartedAt((prev) => prev ? undefined : new Date());
                                                }, disabled: !canGoLive, children: isBroadcasting ? 'Pause' : 'Go Live' })] }), _jsxs(BottomButtonsLower, { children: [_jsx(AudioDeviceButton, { muted: !isAudioOn, onMuteClick: toggleAudio, volumeControllerRef: volumeControllerRef }), _jsx(VideoDeviceButton, { muted: !isVideoOn, onMuteClick: toggleVideo }), _jsx("div", {}), _jsx(Anchor, { ref: settingsButtonAnchorRef, children: _jsx(Button, { type: "button", kind: "secondary", shape: "pill", padding: "none", variant: "dark", icon: _jsx(SvgIcon, { name: "dotDotDot" }), onClick: () => {
                                                        showBroadcastPopup({
                                                            streamId,
                                                            anchorRef: settingsButtonAnchorRef,
                                                            initialQuality: broadcaster?.getVideoEncodingQuality() ??
                                                                'sd',
                                                            onVideoQualityChanged: (quality) => {
                                                                broadcaster?.setVideoEncodingQuality(quality);
                                                            },
                                                        });
                                                    } }) })] })] })] })] })] }));
}
const TopButtons = styled.div `
    display: flex;
    justify-content: space-between;

    button {
        pointer-events: all;
    }
`;
const BottomButtonsLower = styled.div `
    display: grid;
    grid-template-columns: auto auto 1fr auto;
    gap: 0.75rem;

    & > * {
        pointer-events: all;
    }
`;
const BottomButtonsUpper = styled.div `
    display: grid;
    grid-template-columns: 1fr auto;
    gap: 0.75rem;
    margin-bottom: 1.25rem;

    & > * {
        pointer-events: all;
    }
`;
const GoLiveButton = styled(Button) ``;
const BroadcasterHud = styled(Hud) `
    display: grid;
    grid-template-rows: auto 1fr auto;
    padding: 1.25rem;
    box-sizing: border-box;
`;
